import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplate,
  FormTemplateSection,
} from 'data/template/template.types';
import * as types from 'data/securityPolicy/securityPolicy.types';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';
import './security-policy-form.scss';

type Props = {
  template: FormTemplate;
  data: Partial<{
    passwordSettings: types.Password,
    accountLockoutSettings: types.AccountLockout,
    ipAddressBlockingSettings: types.IpAddressBlocking,
  }>;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (
    param: FieldUpdateHandlerParam,
    formSection?: 'passwordSettings' | 'accountLockoutSettings' | 'ipAddressBlockingSettings',
  ) => void;
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => {
    passwordSettings: types.Password,
    accountLockoutSettings: types.AccountLockout,
    ipAddressBlockingSettings: types.IpAddressBlocking
  };
};

export const SECURITY_POLICY_I18N_KEY =
  'securityPolicy.form';

export const SecurityPolicyForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = SECURITY_POLICY_I18N_KEY,
      onFieldUpdate = () => null,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const passwordSettingsRef = useRef<Form>(null);
    const accountLockoutSettingsRef = useRef<Form>(null);
    const ipAddressBlockingSettingsRef = useRef<Form>(null);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
    };

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isPasswordSettingsValid =
        passwordSettingsRef.current?.isValid(forceValidation, forceTouched) ?? true;
      const isAccountLockoutSettingsValid =
        accountLockoutSettingsRef.current?.isValid(forceValidation, forceTouched) ?? true;
      const isIpAddressBlockingSettingsValid =
        ipAddressBlockingSettingsRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isPasswordSettingsValid && isAccountLockoutSettingsValid && isIpAddressBlockingSettingsValid;
    };

    const getValues = () => {
      const formValues = {
        passwordSettings: passwordSettingsRef.current?.getValues() as types.Password,
        accountLockoutSettings: accountLockoutSettingsRef.current?.getValues() as types.AccountLockout,
        ipAddressBlockingSettings: ipAddressBlockingSettingsRef.current?.getValues() as types.IpAddressBlocking,
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const handlePasswordFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'passwordSettings');
    const handleAccountLockoutFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'accountLockoutSettings');
    const handleIpAddressBlockingFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'ipAddressBlockingSettings');

    return (
      <div
        className="security-policy-form"
        data-testid="SecurityPolicyForm"
      >
        <div data-testid="SecurityPolicyFormPasswordSettings">
          <h3 className="security-policy-form-subtitle">{t('securityPolicy.form.passwordSettingsSection')}</h3>
          <FormLayout>
            <Form
              ref={passwordSettingsRef}
              name={template.formId ?? template.key}
              defaults={data.passwordSettings}
              fields={(template.passwordSettings as FormTemplateSection).fields}
              layout={
                (template.passwordSettings as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={handlePasswordFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
        <div data-testid="SecurityPolicyFormAccountLockoutSettings">
          <h3 className="security-policy-form-subtitle">{t('securityPolicy.form.accountLockoutSettingsSection')}</h3>
          <FormLayout>
            <Form
              ref={accountLockoutSettingsRef}
              name={template.formId ?? template.key}
              defaults={data.accountLockoutSettings}
              fields={(template.accountLockoutSettings as FormTemplateSection).fields}
              layout={
                (template.accountLockoutSettings as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={handleAccountLockoutFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
        <div data-testid="SecurityPolicyFormIpAddressBlockingSettings">
          <h3 className="security-policy-form-subtitle">{t('securityPolicy.form.ipAddressBlockingSettingsSection')}</h3>
          <FormLayout>
            <Form
              ref={ipAddressBlockingSettingsRef}
              name={template.formId ?? template.key}
              defaults={data.ipAddressBlockingSettings}
              fields={(template.ipAddressBlockingSettings as FormTemplateSection).fields}
              layout={
                (template.ipAddressBlockingSettings as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={handleIpAddressBlockingFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
      </div>
    );
  },
);

export default SecurityPolicyForm;
